import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  useTheme,
  Dialog,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  TablePagination,
  Tooltip,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import attendancePolicySvg from "assets/folders/leave-types.svg";

import {
  fetchAttendancePolicies,
  createAttendancePolicy,
  updateAttendancePolicy,
  deleteAttendancePolicy,
} from "store/features/company/attendanceManagement/attendancePolicy/attendancePolicyActions";
import {
  selectAttendancePolicies,
  selectAttendancePolicyLoading,
  selectAttendancePolicyPagination,
} from "store/features/company/attendanceManagement/attendancePolicy/attendancePolicySelectors";
import { fetchLeaveTypes } from "store/features/company/leaveManagement/leaveType/leaveTypeActions";
import { selectLeaveTypes } from "store/features/company/leaveManagement/leaveType/leaveTypeSelectors";

const WEEK_DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const AttendancePolicy = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const policies = useSelector(selectAttendancePolicies);
  const loading = useSelector(selectAttendancePolicyLoading);
  const pagination = useSelector(selectAttendancePolicyPagination);
  const leaveTypes = useSelector(selectLeaveTypes);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    shift_name: "",
    week_off_days: [],
    shift_start_time: "09:00:00",
    shift_end_time: "18:00:00",
    grace_period_minutes: 15,
    late_cutoff_time: "11:00:00",
    early_exit_cutoff_time: "17:15:00",
    late_plus_early_equals_absent: true,
    accumulate_lates_and_early_exits: true,
    violation_count_threshold: 3,
    deduction_days: 1.0,
    deduction_leave_type_id: "",
  });

  useEffect(() => {
    dispatch(fetchAttendancePolicies({ page: page + 1, limit: rowsPerPage }));
    dispatch(fetchLeaveTypes());
  }, [dispatch, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (policy = null) => {
    if (policy) {
      let weekOffDays = policy.week_off_days || [];
      if (weekOffDays.length > 0 && typeof weekOffDays[0] === "string") {
        const dayMap = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };
        weekOffDays = weekOffDays
          .map((day) => dayMap[day.toLowerCase()])
          .filter((d) => d !== undefined);
      }

      setEditingPolicy(policy);
      setFormData({
        name: policy.name || "",
        shift_name: policy.shift_name || "",
        week_off_days: weekOffDays,
        shift_start_time: policy.shift_start_time || "09:00:00",
        shift_end_time: policy.shift_end_time || "18:00:00",
        grace_period_minutes: policy.grace_period_minutes ?? 15,
        late_cutoff_time: policy.late_cutoff_time || "11:00:00",
        early_exit_cutoff_time: policy.early_exit_cutoff_time || "17:15:00",
        late_plus_early_equals_absent: Boolean(policy.late_plus_early_equals_absent ?? 1),
        accumulate_lates_and_early_exits: Boolean(policy.accumulate_lates_and_early_exits ?? 1),
        violation_count_threshold: policy.violation_count_threshold ?? 3,
        deduction_days: policy.deduction_days ?? 1.0,
        deduction_leave_type_id: policy.deduction_leave_type_id || "",
      });
    } else {
      setEditingPolicy(null);
      setFormData({
        name: "",
        shift_name: "",
        week_off_days: [],
        shift_start_time: "09:00:00",
        shift_end_time: "18:00:00",
        grace_period_minutes: 15,
        late_cutoff_time: "11:00:00",
        early_exit_cutoff_time: "17:15:00",
        late_plus_early_equals_absent: true,
        accumulate_lates_and_early_exits: true,
        violation_count_threshold: 3,
        deduction_days: 1.0,
        deduction_leave_type_id: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingPolicy(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const payload = {
      name: formData.name,
      shift_name: formData.shift_name,
      week_off_days: formData.week_off_days,
      shift_start_time: formData.shift_start_time,
      shift_end_time: formData.shift_end_time,
      grace_period_minutes: formData.grace_period_minutes,
      late_cutoff_time: formData.late_cutoff_time,
      early_exit_cutoff_time: formData.early_exit_cutoff_time,
      late_plus_early_equals_absent: formData.late_plus_early_equals_absent ? 1 : 0,
      accumulate_lates_and_early_exits: formData.accumulate_lates_and_early_exits ? 1 : 0,
      violation_count_threshold: formData.violation_count_threshold,
      deduction_days: formData.deduction_days,
      deduction_leave_type_id: formData.deduction_leave_type_id || null,
    };

    let result;
    if (editingPolicy) {
      result = await dispatch(
        updateAttendancePolicy(editingPolicy.id, payload),
      );
    } else {
      result = await dispatch(createAttendancePolicy(payload));
    }

    if (result?.success) {
      handleCloseModal();
      dispatch(fetchAttendancePolicies({ page: page + 1, limit: rowsPerPage }));
    }
  };

  const handleDeleteClick = (policy) => {
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (policyToDelete) {
      const result = await dispatch(deleteAttendancePolicy(policyToDelete.id));
      if (result?.success) {
        setDeleteDialogOpen(false);
        setPolicyToDelete(null);
        dispatch(
          fetchAttendancePolicies({ page: page + 1, limit: rowsPerPage }),
        );
      }
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    return dayjs(`2000-01-01T${timeStr}`).format("hh:mm A");
  };

  const getWeekOffLabels = (days) => {
    if (!days || days.length === 0) return "None";
    return days
      .map((day) => {
        // Handle both string and number formats
        const dayValue =
          typeof day === "string" ? convertDayStringToNumber(day) : day;
        const found = WEEK_DAYS.find((d) => d.value === dayValue);
        return found ? found.label : day;
      })
      .join(", ");
  };

  const convertDayStringToNumber = (dayStr) => {
    const map = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    return map[dayStr.toLowerCase()] ?? 0;
  };

  //   if (!hasPermission("attendance_policies.view")) {
  //     return null;
  //   }

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={attendancePolicySvg}
              alt="Attendance Policy"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Attemdance Policies
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "text.secondary",
                }}
              >
                Manage checkin-checkout policies and shift timings
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() =>
                dispatch(
                  fetchAttendancePolicies({
                    page: page + 1,
                    limit: rowsPerPage,
                  }),
                )
              }
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor:
                  theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                "&:hover": {
                  borderColor: "#f59e0b",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "#fffbeb",
                },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "10px",
                px: 2.5,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Policy
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
          }`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 25px -5px rgba(0,0,0,0.3)"
              : "0 10px 25px -5px rgba(0,0,0,0.05)",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Policy Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Shift Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Shift Timing</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Week Off Days</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rules & Penalties</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress sx={{ color: "primary.main" }} />
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                      Loading policies...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography sx={{ color: "text.secondary" }}>
                      No attendance policies found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                policies.map((policy, index) => (
                  <TableRow
                    key={policy.id}
                    component={motion.tr}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    hover
                  >
                    <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>
                        {policy.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{policy.shift_name || "-"}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography sx={{ fontSize: "0.85rem" }}>
                          <strong>In:</strong>{" "}
                          {formatTime(policy.shift_start_time)}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem" }}>
                          <strong>Out:</strong>{" "}
                          {formatTime(policy.shift_end_time)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "4px",
                          backgroundColor: "#d97706",
                          color: "#ffffff",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          fontFamily: "monospace",
                        }}
                      >
                        {getWeekOffLabels(policy.week_off_days)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                          ⏱️ Grace: <strong>{policy.grace_period_minutes ?? 15} mins</strong>
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                          🛑 Absent Cutoff: <strong>{formatTime(policy.late_cutoff_time || "11:00:00")}</strong>
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                          ⚖️ Penalty: <strong>{policy.violation_count_threshold ?? 3} Lates ➔ {policy.deduction_days ?? 1} Day ({policy.deduction_leave_type_name || "LOP"})</strong>
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.85rem" }}>
                      {policy.created_at
                        ? dayjs(policy.created_at).format(
                            "DD MMM YYYY, hh:mm A",
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModal(policy)}
                            sx={{
                              color: "#f59e0b",
                              "&:hover": {
                                backgroundColor: "rgba(245,158,11,0.1)",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(policy)}
                            sx={{
                              color: "#ef4444",
                              "&:hover": {
                                backgroundColor: "rgba(239,68,68,0.1)",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={pagination?.total_records || policies.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "900px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Yellow Banner */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                mb: 3,
              }}
            >
              {editingPolicy
                ? "Edit Attendance Policy"
                : "Add New Attendance Policy"}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Policy Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  required
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Shift Name"
                  value={formData.shift_name}
                  onChange={(e) =>
                    handleFormChange("shift_name", e.target.value)
                  }
                  required
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Shift Start Time"
                  type="time"
                  value={formData.shift_start_time}
                  onChange={(e) =>
                    handleFormChange("shift_start_time", e.target.value)
                  }
                  required
                  size="small"
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Shift End Time"
                  type="time"
                  value={formData.shift_end_time}
                  onChange={(e) =>
                    handleFormChange("shift_end_time", e.target.value)
                  }
                  required
                  size="small"
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    mb: 1,
                    color: "text.secondary",
                  }}
                >
                  Week Off Days
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    p: 1.5,
                    border: `1px solid ${
                      theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                    }`,
                    borderRadius: "10px",
                    minHeight: "56px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                  }}
                >
                  {WEEK_DAYS.map((day) => {
                    const isSelected = formData.week_off_days.includes(
                      day.value,
                    );
                    return (
                      <Box
                        key={day.value}
                        onClick={() => {
                          const newSelected = isSelected
                            ? formData.week_off_days.filter(
                                (d) => d !== day.value,
                              )
                            : [...formData.week_off_days, day.value];
                          handleFormChange("week_off_days", newSelected);
                        }}
                        sx={{
                          px: 2,
                          py: 0.75,
                          borderRadius: "6px",
                          backgroundColor: isSelected
                            ? "#d97706"
                            : "transparent",
                          color: isSelected ? "#ffffff" : "text.primary",
                          border: `1px solid ${
                            isSelected
                              ? "#d97706"
                              : theme.palette.mode === "dark"
                              ? "#334155"
                              : "#e5e7eb"
                          }`,
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                          fontWeight: isSelected ? 600 : 400,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "#b45309"
                              : "rgba(217, 119, 6, 0.1)",
                            borderColor: isSelected ? "#b45309" : "#d97706",
                          },
                        }}
                      >
                        {day.label}
                      </Box>
                    );
                  })}
                </Box>
              </Grid>

              {/* Dynamic Rules Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip
                    label="Dynamic Rules & Violation Penalties"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "#f59e0b",
                      color: "#ffffff",
                    }}
                  />
                </Divider>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Grace Period (Minutes)"
                  type="number"
                  value={formData.grace_period_minutes}
                  onChange={(e) =>
                    handleFormChange("grace_period_minutes", e.target.value)
                  }
                  size="small"
                  helperText="Grace time after shift start"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Late Mark Cutoff Time"
                  type="time"
                  value={formData.late_cutoff_time}
                  onChange={(e) =>
                    handleFormChange("late_cutoff_time", e.target.value)
                  }
                  size="small"
                  helperText="Check-in after this time = Absent"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Early Exit Cutoff Time"
                  type="time"
                  value={formData.early_exit_cutoff_time}
                  onChange={(e) =>
                    handleFormChange("early_exit_cutoff_time", e.target.value)
                  }
                  size="small"
                  helperText="Check-out before this time = Early Exit"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="Deduction Leave Type"
                  value={formData.deduction_leave_type_id}
                  onChange={(e) =>
                    handleFormChange("deduction_leave_type_id", e.target.value)
                  }
                  size="small"
                  helperText="Leave pool to deduct penalty from"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                >
                  <MenuItem value="">
                    <em>Unpaid / Loss of Pay (LOP)</em>
                  </MenuItem>
                  {leaveTypes?.map((lt) => (
                    <MenuItem key={lt.id} value={lt.id}>
                      {lt.name} ({lt.code || "Leave"})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Violations Threshold (Count)"
                  type="number"
                  value={formData.violation_count_threshold}
                  onChange={(e) =>
                    handleFormChange(
                      "violation_count_threshold",
                      e.target.value,
                    )
                  }
                  size="small"
                  helperText="e.g. 3 Lates or Early Exits"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Deduction Days"
                  type="number"
                  inputProps={{ step: "0.5" }}
                  value={formData.deduction_days}
                  onChange={(e) =>
                    handleFormChange("deduction_days", e.target.value)
                  }
                  size="small"
                  helperText="Number of leave days to deduct"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.late_plus_early_equals_absent}
                      onChange={(e) =>
                        handleFormChange(
                          "late_plus_early_equals_absent",
                          e.target.checked,
                        )
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#f59e0b",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#f59e0b",
                          },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Same Day Violation: Late Check-in + Early Departure = Mark Absent
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.accumulate_lates_and_early_exits}
                      onChange={(e) =>
                        handleFormChange(
                          "accumulate_lates_and_early_exits",
                          e.target.checked,
                        )
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#f59e0b",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#f59e0b",
                          },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Automatically Accumulate Monthly Lates/Early Exits & Deduct Leaves
                    </Typography>
                  }
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleCloseModal}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  boxShadow: "0 6px 16px rgba(245, 158, 11, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              {editingPolicy ? "Update" : "Create"}
            </Button>
          </Box>
        </motion.div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            <IconButton
              onClick={() => setDeleteDialogOpen(false)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#f59e0b",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 2,
              }}
            >
              Delete Attendance Policy
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to delete
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{policyToDelete?.name}"
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              This action cannot be undone. All associated data will be
              permanently removed.
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  boxShadow: "0 6px 16px rgba(239, 68, 68, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Delete
            </Button>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default AttendancePolicy;
